import { z } from "zod";
import { CryptoMarket } from "../api/crypto-api";
import { NftCollection, NftInfoZ } from "../schema/SolanaSchema";
import { TokenProgram } from "../schema/SolanaTokenProgram";
import { Solana } from "../solana/solana-base";
import { Base58Z, IsoDateZ } from "./data-types";
import { SolanaStaking } from "./solana-staking";

export namespace SolanaTransaction {
  /**
   * We use the passive voice for transactions since it's unclear who is taking the action.
   *
   * For example, an NFT sale will have multiple parties:
   * 1. the buyer who paid SOL and acquired the NFT
   * 2. the seller who listed the NFT and is receiving the majority of the SOL
   * 3. the marketplace royalty account which takes a fee of the transaction
   * 4. the marketplace program which actually carried out the transaction
   * 5. the creator who received a royalty for every sale
   *
   * This transaction will appear different depending on the context (who is looking at the tx).
   * That's why we use `nft_bought` rather than `sold_nft`, `received_royalties`, etc.
   */
  export enum Type {
    // This transaction did not go through
    Failed = "failed",
    // One account sends a coin to another account without receiving SOL for sending the coin
    CoinMoved = "coin_moved",
    // This is for a token swap on an exchange like Raydium or Orca
    CoinsSwapped = "coins_swapped",
    GlowIdRegistered = "glow_id_registered",
    GlowIdUpdated = "glow_id_updated",
    // An NFT was bought on a marketplace
    NftBought = "nft_bought",
    // An NFT was listed for purchase on a marketplace
    NftListed = "nft_listed",
    // A new price was set
    NftListingUpdated = "nft_listing_updated",
    NftBidCreated = "nft_bid_created",
    NftBidCanceled = "nft_bid_canceled",
    // Deposit / withdraw from NFT marketplace balance
    NftMarketplaceDeposited = "nft_marketplace_deposited",
    NftMarketplaceWithdrawn = "nft_marketplace_withdrawn",

    NftokenCollectionCreated = "nftoken_collection_created",
    NftokenCollectionUpdated = "nftoken_collection_updated",

    // An NFT was minted (often using Metaplex's CandyMachine)
    NftMinted = "nft_minted",
    // An NFT was transferred between accounts without a payment
    NftMoved = "nft_moved",
    NftsMoved = "nfts_moved",
    // An NFT was unlisted for purchase on a marketplace
    NftUnlisted = "nft_unlisted",
    // SOL was moved from one account to another without other tokens
    SolMoved = "sol_moved",
    SolStaked = "sol_staked",
    SolUnstaked = "sol_unstaked",
    StakedSolWithdrawn = "withdrew_staked_sol",
    SubaccountsSetUp = "subaccounts_set_up",
    SubaccountsCleanedUp = "subaccounts_cleaned_up",
    TokenBurned = "token_burned",
    Vote = "vote",

    Unknown = "unknown",
  }

  /**
   * This is based on Solana's TransactionConfirmationStatus, but it's extended to handle a missing
   * transaction in two ways.
   *
   * A missing transaction is either:
   * 1. in-flight — if the tx on the way to the RPC node since it has just been sent
   * 2. ignored - if the tx has not be acknowledged and the blockhash is now old
   */
  export enum Status {
    Processed = "processed",
    Confirmed = "confirmed",
    Finalized = "finalized",
    InFlight = "in-flight",
    Ignored = "ignored",
  }

  export const SwappedCoinZ = z.union([
    z.object({
      type: z.literal("coin"),
      balance_delta: Solana.TokenAmountZ,
      coin: TokenProgram.CoinInfoZ,
    }),
    z.object({
      type: z.literal("sol"),
      balance_delta: Solana.SolAmountZ,
    }),
  ]);
  export type SwappedCoin = z.infer<typeof SwappedCoinZ>;

  export const SummaryZ = z.union([
    z.object({
      transaction_type: z.literal(Type.Failed),
    }),
    z.object({
      transaction_type: z.literal(Type.Vote),
    }),
    z.object({
      transaction_type: z.literal(Type.SolMoved),
      sender: Solana.AddressZ,
      recipient: Solana.AddressZ,
      amount: Solana.SolAmountZ,
    }),
    z.object({
      transaction_type: z.literal(Type.SolStaked),
      staker: Solana.AddressZ,
      stake_account: Solana.AddressZ,
      amount: Solana.SolAmountZ,
      vote_address: Solana.AddressZ,
      validator: SolanaStaking.ValidatorZ,
    }),
    z.object({
      transaction_type: z.literal(Type.Unknown),
    }),
    z.object({
      transaction_type: z.literal(Type.NftMarketplaceDeposited),
      amount: Solana.SolAmountZ,
      marketplace: z.string(),
      marketplace_value: Solana.MarketplaceZ,
    }),
    z.object({
      transaction_type: z.literal(Type.NftMarketplaceWithdrawn),
      amount: Solana.SolAmountZ,
      marketplace: z.string(),
      marketplace_value: Solana.MarketplaceZ,
    }),
    z.object({
      transaction_type: z.literal(Type.NftBidCanceled),
      bidder: Solana.AddressZ,
      marketplace: z.string(),
      marketplace_program: Solana.AddressZ,
      marketplace_value: Solana.MarketplaceZ,
      previous_offer: Solana.SolAmountZ,
      nft: NftInfoZ,
    }),
    z.object({
      transaction_type: z.literal(Type.NftBidCreated),
      bidder: Solana.AddressZ,
      marketplace: z.string(),
      marketplace_program: Solana.AddressZ,
      marketplace_value: Solana.MarketplaceZ,
      offer: Solana.SolAmountZ,
      nft: NftInfoZ,
    }),
    z.object({
      transaction_type: z.literal(Type.NftUnlisted),
      holder: Solana.AddressZ,
      // For historical reasons we send `marketplace` and `marketplace_value` here.
      marketplace: z.string(),
      marketplace_value: Solana.MarketplaceZ,
      marketplace_program: Solana.AddressZ,
      nft: NftInfoZ,
    }),
    z.object({
      transaction_type: z.literal(Type.SolUnstaked),
      staker: Solana.AddressZ,
      amount: Solana.SolAmountZ,
    }),
    z.object({
      transaction_type: z.literal(Type.StakedSolWithdrawn),
      staker: Solana.AddressZ,
      // This exists for legacy reasons. We previously only supported one stake
      // account per withdraw instruction.
      stake_account: Solana.AddressZ,
      stake_account_addresses: z.array(Solana.AddressZ),
      amount: Solana.SolAmountZ,
    }),
    z.object({
      transaction_type: z.literal(Type.NftBought),
      buyer: Solana.AddressZ,
      seller: Solana.AddressZ.nullable(),
      purchase_amount: Solana.SolAmountZ,
      marketplace: z.string(),
      marketplace_value: Solana.MarketplaceZ,
      marketplace_program: Solana.AddressZ,
      nft: NftInfoZ,
      seller_proceeds: Solana.SolAmountZ.nullable(),
      creator_proceeds: Solana.SolAmountZ.nullable(),
      marketplace_fee: Solana.SolAmountZ.nullable(),
    }),
    z.object({
      holder: Solana.AddressZ,
      list_price: Solana.SolAmountZ,
      marketplace: z.string(),
      marketplace_value: Solana.MarketplaceZ,
      marketplace_program: Solana.AddressZ,
      nft: NftInfoZ,
      price_address: Solana.AddressZ.nullable(),
      transaction_type: z.literal(Type.NftListed),
    }),
    z.object({
      transaction_type: z.literal(Type.NftokenCollectionCreated),
      creator: Solana.AddressZ,
      collection: z.any() as z.Schema<NftCollection.BasicInfo>,
    }),
    z.object({
      transaction_type: z.literal(Type.NftokenCollectionUpdated),
      creator: Solana.AddressZ,
      collection: z.any() as z.Schema<NftCollection.BasicInfo>,
    }),
    z.object({
      transaction_type: z.literal(Type.NftMinted),
      candy_machine: Solana.AddressZ.nullable(),
      minter: Solana.AddressZ,
      minting_fee: Solana.SolAmountZ,
      nft: NftInfoZ,
    }),
    z.object({
      transaction_type: z.literal(Type.NftListingUpdated),
      holder: Solana.AddressZ,
      marketplace: z.string(),
      marketplace_value: Solana.MarketplaceZ,
      marketplace_program: Solana.AddressZ,
      new_list_price: Solana.SolAmountZ,
      nft: NftInfoZ,
    }),
    z.object({
      transaction_type: z.literal(Type.NftMoved),
      sender: Solana.AddressZ,
      recipient: Solana.AddressZ,
      nft: NftInfoZ,
    }),
    z.object({
      transaction_type: z.literal(Type.NftsMoved),
      sender: Solana.AddressZ,
      recipient: Solana.AddressZ,
      nfts: z.array(NftInfoZ),
    }),
    z.object({
      transaction_type: z.literal(Type.CoinMoved),
      sender: Solana.AddressZ,
      recipient: Solana.AddressZ,
      coin: TokenProgram.CoinInfoZ,
    }),
    z.object({
      transaction_type: z.literal(Type.GlowIdRegistered),
      controller: Solana.AddressZ,
      handle: z.string(),
      image: z.string(),
      name: z.string(),
      fee: Solana.SolAmountZ,
      invited_by_handle: z.string(),
      invited_by_address: Solana.AddressZ,
      invited_by_reward: Solana.SolAmountZ,
      payment_type: z.union([z.literal("usdc"), z.literal("sol")]),
      fee_usdc: Solana.TokenAmountZ,
      invited_by_reward_usdc: Solana.TokenAmountZ,
    }),
    z.object({
      transaction_type: z.literal(Type.GlowIdUpdated),
      /**
       *  The wallet that controls the Glow ID.
       *  Right now it's the same address the Glow ID resolves to,
       *  in the future, we'll add the `resolved` field  thus allowing
       *  changing the address the Glow ID resolves to without changing the `controller`.
       */
      controller: Solana.AddressZ,
      handle: z.string(),
      image: z.string(),
      name: z.string(),
    }),
    z.object({
      transaction_type: z.literal(Type.CoinsSwapped),
      holder: Solana.AddressZ,
      token_sent: SwappedCoinZ,
      token_received: SwappedCoinZ,
      exchange: z.string().nullable(),
    }),
    z.object({
      transaction_type: z.literal(Type.SubaccountsCleanedUp),
      amount: Solana.SolAmountZ,
      subaccounts: z.record(z.union([TokenProgram.CoinInfoZ, NftInfoZ])),
    }),
    z.object({
      transaction_type: z.literal(Type.SubaccountsSetUp),
      subaccounts: z.record(z.union([TokenProgram.CoinInfoZ, NftInfoZ])),
      amount: Solana.SolAmountZ,
    }),
    z.object({
      transaction_type: z.literal(Type.TokenBurned),
      tokens: z.array(z.union([TokenProgram.CoinInfoZ, NftInfoZ])),
    }),
  ]);

  // Add more metadata here...
  export type Summary = z.infer<typeof SummaryZ>;

  const TransactionAccountInfoZ = z.object({
    pubkey: Solana.AddressZ,
    signer: z.boolean().nullable(),
    writable: z.boolean().nullable(),
    sol: z.object({
      // We don't have the pre and post amounts if the account was not
      // specified in the accounts list for the transaction.
      pre: Solana.SolAmountZ.nullable(),
      post: Solana.SolAmountZ.nullable(),
      delta: Solana.SolAmountZ,
    }),
    // Token mint → balance info
    token_to_balance: z.record(
      z.object({
        mint: Solana.AddressZ,
        pre: Solana.TokenAmountZ.nullable(),
        post: Solana.TokenAmountZ.nullable(),
        delta: Solana.TokenAmountZ,
      }),
    ),
  });

  const TransactionZ = z.object({
    api_id: Base58Z, // Used for pagination
    signature: Base58Z,
    timestamp: IsoDateZ,
    slot: z.number(),
    account_to_info: z.record(TransactionAccountInfoZ.nullable()),
    token_account_to_mint: z.record(z.string()),
    // TODO: We need to tighten this type up, so the code that works
    //       with this structure has to check for `undefined` values.
    token_to_info: z.record(z.union([TokenProgram.CoinInfoZ, NftInfoZ])),
    sol_exchange_rate: CryptoMarket.ExchangeRateZ,
    token_to_exchange_rate: z.record(CryptoMarket.ExchangeRateZ.nullable()),
    fee: Solana.SolAmountZ.nullable(),
    fee_payer: Solana.AddressZ,
    note: z.string().nullable(),
    summary: SummaryZ,
    memos: z.array(z.string()),
    status: z.object({
      is_success: z.boolean(),
      message: z.string(),
    }),
    error: z
      .object({
        message: z.string(),
        code: z.nativeEnum(Solana.TransactionError),
      })
      .nullable(),
  });

  export type Transaction = z.infer<typeof TransactionZ>;
}
